import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DataTable } from "@/components/ui/data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Bird } from "lucide-react";
import { useEffect, useState } from "react";
import { PoultrySearch } from "./PoultrySearch";
import { getPoultryColumns } from "./PoultryColumns";
import { PoultryItem } from "./types";
import { TablePagination } from "@/components/ui/data-table/TablePagination";

interface PoultryInventoryProps {
  onLossClick: (item: PoultryItem) => void;
  onReturnClick: (item: PoultryItem) => void;
}

export const PoultryInventory = ({ onLossClick, onReturnClick }: PoultryInventoryProps) => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data: poultryItems, isLoading } = useQuery({
    queryKey: ["poultryInventory"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data: stockItems, error } = await supabase
        .from("stock_items")
        .select("id, data, quantity")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return stockItems as PoultryItem[];
    },
  });

  useEffect(() => {
    const channel = supabase
      .channel('stock-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stock_items'
        },
        (payload) => {
          console.log('Change received!', payload);
          queryClient.invalidateQueries({ queryKey: ['poultryInventory'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const columns = getPoultryColumns(onLossClick, onReturnClick);

  const filteredItems = poultryItems?.filter((item) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      !searchQuery ||
      item.data.Race?.toLowerCase().includes(searchLower) ||
      item.data.name?.toLowerCase().includes(searchLower) ||
      item.data.Age?.toLowerCase().includes(searchLower) ||
      item.data.Santé?.toLowerCase().includes(searchLower)
    );
  });

  const totalItems = filteredItems?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = filteredItems?.slice(startIndex, startIndex + itemsPerPage);

  if (isLoading) {
    return <div>Chargement de l'inventaire...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Bird className="h-5 w-5 text-primary" />
          <CardTitle>Inventaire des Volailles</CardTitle>
        </div>
        <PoultrySearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      </CardHeader>
      <CardContent>
        <DataTable columns={columns} data={paginatedItems || []} />
        {totalPages > 1 && (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </CardContent>
    </Card>
  );
};